import { supabase } from '../lib/supabase';

/**
 * Notification Service
 * Handles all email notifications for file uploads, tasks, and meetings
 */

/**
 * Sends file upload notification to team members, pcf_admin, mentor, and city official
 * @param {Object} uploadData - File upload details
 */
export const sendFileUploadNotification = async (uploadData) => {
  try {
    const {
      uploaderName,
      teamName,
      teamNumber,
      fileName,
      timestamp,
      stage,
      fileUrl,
      teamId
    } = uploadData;

    console.log('📧 Sending file upload notification...', { fileName, teamId });

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-file-upload-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          uploaderName,
          teamName,
          teamNumber,
          fileName,
          timestamp,
          stage,
          fileUrl,
          teamId
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ File upload notification failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ File upload notification sent successfully');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending file upload notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * NEW: Send notification for deliverable completion
 * @param {Object} notificationData - Notification details
 */
export const sendDeliverableCompletionNotification = async (notificationData) => {
  try {
    const { teamId, deliverableName, teamName } = notificationData;

    console.log('📧 Sending deliverable completion notification...', { deliverableName, teamId });

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-file-upload-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          uploaderName: 'System',
          teamName: teamName,
          teamNumber: '',
          fileName: `Deliverable Completed: ${deliverableName}`,
          timestamp: new Date()?.toISOString(),
          stage: 'Deliverable Completion',
          fileUrl: '',
          teamId: teamId,
          notificationType: 'deliverable_completion',
          message: `Team ${teamName} has completed the deliverable: ${deliverableName}`
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Deliverable completion notification failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Deliverable completion notification sent successfully');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending deliverable completion notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * NEW: Send notification for deliverable due date approaching
 * @param {Object} notificationData - Notification details
 */
export const sendDeliverableDueDateNotification = async (notificationData) => {
  try {
    const { teamId, deliverableName, teamName, dueDate, hoursRemaining } = notificationData;

    console.log('📧 Sending deliverable due date notification...', { deliverableName, teamId });

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-file-upload-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          uploaderName: 'System',
          teamName: teamName,
          teamNumber: '',
          fileName: `Deliverable Due: ${deliverableName}`,
          timestamp: new Date()?.toISOString(),
          stage: 'Deliverable Due Date Alert',
          fileUrl: '',
          teamId: teamId,
          notificationType: 'deliverable_due_date',
          message: `Reminder: Deliverable "${deliverableName}" for team ${teamName} is due in ${hoursRemaining} hours (${new Date(dueDate)?.toLocaleDateString()})`
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Deliverable due date notification failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Deliverable due date notification sent successfully');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending deliverable due date notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends task creation notification
 * @param {Object} taskData - Task details
 */
export const sendTaskCreationNotification = async (taskData) => {
  try {
    const { data: creator } = await supabase?.from('user_profiles')?.select('full_name, phone')?.eq('id', taskData?.creator_id)?.single();

    const { data: assignee } = await supabase?.from('user_profiles')?.select('full_name, phone')?.eq('id', taskData?.assignee_id)?.single();

    // Send email notification
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-task-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          taskDetails: {
            ...taskData,
            creator_name: creator?.full_name,
            assignee_name: assignee?.full_name
          },
          notificationType: 'created',
          teamId: taskData?.team_id
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Task creation notification failed:', result);
      return { success: false, error: result?.error };
    }

    // Send SMS notification if assignee has phone number
    if (assignee?.phone) {
      try {
        const smsMessage = `New Task Assigned!\n\nTask: ${taskData?.title}\nCreated by: ${creator?.full_name}\nPriority: ${taskData?.priority}\n\nCheck your dashboard for details.`;
        
        await sendTaskSMS(assignee?.phone, smsMessage, taskData?.title);
        console.log('✅ Task creation SMS sent to assignee');
      } catch (smsError) {
        console.warn('⚠️ Task creation SMS failed:', smsError);
      }
    }

    console.log('✅ Task creation notification sent');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending task creation notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends task status change notification
 * @param {Object} statusData - Status change details
 */
export const sendTaskStatusChangeNotification = async (statusData) => {
  try {
    const { data: changedBy } = await supabase?.from('user_profiles')?.select('full_name, phone')?.eq('id', statusData?.changed_by_id)?.single();

    // Get team members who should be notified
    const { data: teamMembers } = await supabase?.from('user_profiles')?.select('id, full_name, phone')?.eq('team_id', statusData?.team_id);

    // Send email notification
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-task-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          taskDetails: {
            ...statusData,
            changed_by: changedBy?.full_name,
            timestamp: new Date()?.toISOString()
          },
          notificationType: 'status_changed',
          teamId: statusData?.team_id
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Task status notification failed:', result);
      return { success: false, error: result?.error };
    }

    // Send SMS to all team members with phone numbers
    const smsPromises = teamMembers
      ?.filter(member => member?.phone)
      ?.map(async (member) => {
        try {
          const smsMessage = `Task Status Update!\n\nTask: ${statusData?.title}\nStatus: ${statusData?.previous_status} → ${statusData?.new_status}\nChanged by: ${changedBy?.full_name}\n\nCheck your dashboard for details.`;
          
          await sendTaskSMS(member?.phone, smsMessage, statusData?.title);
          console.log(`✅ Task status SMS sent to ${member?.full_name}`);
        } catch (smsError) {
          console.warn(`⚠️ Task status SMS failed for ${member?.full_name}:`, smsError);
        }
      });

    if (smsPromises?.length > 0) {
      await Promise.allSettled(smsPromises);
    }

    console.log('✅ Task status notification sent');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending task status notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends task due date reminder
 * @param {Object} taskData - Task details
 */
export const sendTaskDueDateReminder = async (taskData) => {
  try {
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-task-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          taskDetails: taskData,
          notificationType: 'due_soon',
          teamId: taskData?.team_id
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Task due reminder failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Task due reminder sent');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending task due reminder:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Checks for tasks due within 6 hours and sends reminders
 * Should be called periodically (e.g., every 5-15 minutes)
 */
export const checkAndSendTaskDueReminders = async () => {
  try {
    const sixHoursFromNow = new Date();
    sixHoursFromNow?.setHours(sixHoursFromNow?.getHours() + 6);

    const { data: tasks, error } = await supabase?.from('team_tasks')?.select('*')?.not('status', 'eq', 'Completed')?.not('due_date', 'is', null)?.lte('due_date', sixHoursFromNow?.toISOString())?.is('reminder_sent', false);

    if (error) throw error;

    console.log(`📋 Found ${tasks?.length || 0} tasks due within 6 hours`);

    for (const task of tasks || []) {
      await sendTaskDueDateReminder(task);
      
      // Mark reminder as sent
      await supabase?.from('team_tasks')?.update({ reminder_sent: true })?.eq('id', task?.id);
    }

    return { success: true, tasksProcessed: tasks?.length || 0 };
  } catch (error) {
    console.error('Error checking task due reminders:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends 1-hour meeting reminder to all attendees
 * @param {Object} meetingData - Meeting details
 */
export const sendMeetingReminder = async (meetingData) => {
  try {
    const { data: organizer } = await supabase?.from('user_profiles')?.select('full_name')?.eq('id', meetingData?.organizer_id)?.single();

    // Fetch attendee details
    const { data: userProfiles } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.in('id', meetingData?.attendee_ids);

    const userEmails = userProfiles?.map(p => p?.email) || [];
    const { data: authorizedEmails } = await supabase?.from('role_email_authorizations')?.select('email, role')?.in('email', userEmails)?.eq('is_active', true);

    const attendees = userProfiles?.map(profile => {
      const authorizedEmail = authorizedEmails?.find(auth => auth?.email === profile?.email);
      return {
        name: profile?.full_name,
        email: authorizedEmail?.email || profile?.email
      };
    });

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-meeting-reminder`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          attendees,
          meetingDetails: {
            title: meetingData?.title,
            description: meetingData?.description,
            date: meetingData?.meeting_date,
            time: meetingData?.meeting_time,
            duration: meetingData?.duration_minutes,
            location: meetingData?.location_details || meetingData?.location_type,
            googleMeetLink: meetingData?.google_meet_link,
            organizer: organizer?.full_name
          }
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Meeting reminder failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Meeting reminder sent');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending meeting reminder:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Checks for meetings starting within 1 hour and sends reminders
 * Should be called periodically (e.g., every 5-15 minutes)
 */
export const checkAndSendMeetingReminders = async () => {
  try {
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    
    // Format for database comparison
    const currentDate = now?.toISOString()?.split('T')?.[0];
    const futureDate = oneHourFromNow?.toISOString()?.split('T')?.[0];
    const currentTime = now?.toTimeString()?.split(' ')?.[0];
    const futureTime = oneHourFromNow?.toTimeString()?.split(' ')?.[0];

    const { data: meetings, error } = await supabase?.from('meetings')?.select('*')?.eq('status', 'scheduled')?.is('reminder_sent', false)?.or(`and(meeting_date.eq.${currentDate},meeting_time.gte.${currentTime},meeting_time.lte.${futureTime}),and(meeting_date.eq.${futureDate},meeting_time.lte.${futureTime})`);

    if (error) throw error;

    console.log(`📅 Found ${meetings?.length || 0} meetings starting within 1 hour`);

    for (const meeting of meetings || []) {
      await sendMeetingReminder(meeting);
      
      // Mark reminder as sent
      await supabase?.from('meetings')?.update({ reminder_sent: true })?.eq('id', meeting?.id);
    }

    return { success: true, meetingsProcessed: meetings?.length || 0 };
  } catch (error) {
    console.error('Error checking meeting reminders:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends SMS notification using Twilio edge function
 * @param {string} phoneNumber - Phone number in E.164 format
 * @param {string} message - SMS message content
 * @param {string} taskTitle - Task title for logging
 */
export const sendTaskSMS = async (phoneNumber, message, taskTitle = 'Task Notification') => {
  try {
    if (!phoneNumber) {
      console.warn('⚠️ No phone number provided for SMS notification');
      return { success: false, error: 'No phone number' };
    }

    // Validate phone number format (basic check)
    if (!phoneNumber?.startsWith('+')) {
      console.warn('⚠️ Phone number must be in E.164 format (e.g., +1234567890)');
      return { success: false, error: 'Invalid phone format' };
    }

    console.log('📱 Sending task SMS...', { to: phoneNumber, taskTitle });

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/twilio-messaging`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: message,
          messageId: `task_${Date.now()}`
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Task SMS failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Task SMS sent successfully:', result?.messageSid);
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending task SMS:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Creates a notification record in the database
 * @param {Object} notificationData - Notification details
 */
export const createNotification = async (notificationData) => {
  try {
    const { data, error } = await supabase?.from('notifications')?.insert([{
      team_id: notificationData?.team_id,
      user_id: notificationData?.user_id,
      type: notificationData?.type,
      title: notificationData?.title,
      message: notificationData?.message,
      related_id: notificationData?.related_id || notificationData?.reference_id,
      related_type: notificationData?.related_type || notificationData?.reference_type,
      created_at: new Date()?.toISOString()
    }])?.select()?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Fetches notifications for a specific team
 * @param {string} teamId - Team ID
 * @param {number} limit - Number of notifications to fetch
 */
export const getTeamNotifications = async (teamId, limit = 5) => {
  try {
    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('team_id', teamId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching team notifications:', error);
    return { success: false, error: error?.message, data: [] };
  }
};

/**
 * Get user notifications with pagination
 * @param {string} userId - User ID
 * @param {number} limit - Number of notifications to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<{success: boolean, data: Array, error: string}>}
 */
export const getUserNotifications = async (userId, limit = 20, offset = 0) => {
  try {
    if (!userId) {
      return { success: false, data: [], error: 'User ID is required' };
    }

    // Get user's notification email from profile
    const { data: userProfile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('notification_email, email')
      ?.eq('id', userId)
      ?.single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return { success: false, data: [], error: 'Failed to fetch user profile' };
    }

    // Use notification_email if set, otherwise fall back to email
    const recipientEmail = userProfile?.notification_email || userProfile?.email;

    // Fetch notifications where user_id matches OR recipient_email matches
    const { data, error } = await supabase
      ?.from('notifications')
      ?.select('*')
      ?.or(`user_id.eq.${userId},recipient_email.eq.${recipientEmail}`)
      ?.order('created_at', { ascending: false })
      ?.range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching notifications:', error);
      return { success: false, data: [], error: error?.message };
    }

    return { success: true, data: data || [], error: null };
  } catch (error) {
    console.error('Error in getUserNotifications:', error);
    return { success: false, data: [], error: error?.message };
  }
};

/**
 * Fetches notifications from last 30 days for a team
 * @param {string} teamId - Team ID
 */
export const getTeamNotificationsLast30Days = async (teamId) => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);

    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('team_id', teamId)?.gte('created_at', thirtyDaysAgo?.toISOString())?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching 30-day notifications:', error);
    return { success: false, error: error?.message, data: [] };
  }
};

/**
 * Marks notifications as seen for a user
 * @param {string} userId - User ID
 * @param {Array<string>} notificationIds - Array of notification IDs to mark as seen
 */
export const markNotificationsAsSeen = async (userId, notificationIds) => {
  try {
    const { error } = await supabase?.from('notifications')?.update({ 
        seen: true,
        seen_at: new Date()?.toISOString()
      })?.in('id', notificationIds)?.eq('user_id', userId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error marking notifications as seen:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Gets unseen notification count for a user
 * @param {string} userId - User ID
 */
export const getUnseenNotificationCount = async (userId) => {
  try {
    if (!userId) {
      console.warn('⚠️ No userId provided for unseen notification count');
      return { success: true, count: 0 };
    }

    const { count, error } = await supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('seen', false);

    if (error) {
      console.error('Error in getUnseenNotificationCount:', error);
      throw error;
    }

    return { success: true, count: count || 0 };
  } catch (error) {
    console.error('Error getting unseen count:', error);
    return { success: false, count: 0, error: error?.message };
  }
};

/**
 * Deletes notifications related to a task
 * @param {string} taskId - Task ID
 */
export const deleteTaskNotifications = async (taskId) => {
  try {
    const { error } = await supabase?.from('notifications')?.delete()?.eq('related_id', taskId)?.eq('related_type', 'task');

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error deleting task notifications:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Create admin notification for file upload
 * @param {string} teamId - Team ID that uploaded
 * @param {string} stageName - Stage name
 * @param {string} teamName - Team name
 */
export const createFileUploadNotification = async (teamId, stageName, teamName) => {
  try {
    // Get all PCF admin users
    const { data: admins, error: adminError } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('role', 'pcf-admin');

    if (adminError) throw adminError;

    // Create notification for each admin
    const notifications = admins?.map(admin => ({
      user_id: admin?.id,
      type: 'file_uploaded',
      title: 'Team File Upload',
      message: `${teamName} uploaded a file for ${stageName}`,
      related_type: 'team',
      related_id: teamId
    }));

    const { error } = await supabase
      ?.from('notifications')
      ?.insert(notifications);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error creating file upload notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Create admin notification for stage completion
 * @param {string} teamId - Team ID that completed stage
 * @param {string} stageName - Stage name
 * @param {string} teamName - Team name
 */
export const createStageCompletionNotification = async (teamId, stageName, teamName) => {
  try {
    // Get all PCF admin users
    const { data: admins, error: adminError } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('role', 'pcf-admin');

    if (adminError) throw adminError;

    // Create notification for each admin
    const notifications = admins?.map(admin => ({
      user_id: admin?.id,
      type: 'stage_completed',
      title: 'Stage Completed',
      message: `${teamName} completed all uploads for ${stageName}`,
      related_type: 'team',
      related_id: teamId
    }));

    const { error } = await supabase
      ?.from('notifications')
      ?.insert(notifications);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error creating stage completion notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Create admin notification for deadline warning
 * @param {string} teamId - Team ID approaching deadline
 * @param {string} stageName - Stage name
 * @param {string} teamName - Team name
 */
export const createDeadlineWarningNotification = async (teamId, stageName, teamName) => {
  try {
    // Get all PCF admin users
    const { data: admins, error: adminError } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('role', 'pcf-admin');

    if (adminError) throw adminError;

    // Create notification for each admin
    const notifications = admins?.map(admin => ({
      user_id: admin?.id,
      type: 'deadline_warning',
      title: 'Deadline Warning',
      message: `${teamName}'s stage ${stageName} is 1 day away from deadline and not completed`,
      related_type: 'team',
      related_id: teamId
    }));

    const { error } = await supabase
      ?.from('notifications')
      ?.insert(notifications);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error creating deadline warning notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Create admin notification when meeting includes admin
 * @param {string} meetingId - Meeting ID
 * @param {string} meetingTitle - Meeting title
 * @param {string} adminId - Admin user ID
 */
export const createMeetingNotificationForAdmin = async (meetingId, meetingTitle, adminId) => {
  try {
    const { error } = await supabase
      ?.from('notifications')
      ?.insert({
        user_id: adminId,
        type: 'meeting_scheduled',
        title: 'Meeting Scheduled',
        message: `You have been added as an attendee to "${meetingTitle}"`,
        related_type: 'meeting',
        related_id: meetingId
      });

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error creating meeting notification for admin:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends notification emails to multiple recipients using Resend edge function
 * @param {Object} emailData - Email notification details
 * 
 * ⚠️ PRIVACY: All recipient emails are sent via BCC to prevent recipients from seeing each other's addresses
 */
export const sendNotificationEmails = async (emailData) => {
  try {
    const {
      recipients, // Array of { email, full_name }
      subject,
      message,
      priority,
      notificationType
    } = emailData;

    console.log('📧 Sending notification emails (BCC) to', recipients?.length, 'recipients...');

    // Extract email addresses from recipients array
    const recipientEmails = recipients?.map(r => r?.email)?.filter(email => email) || [];

    if (recipientEmails?.length === 0) {
      console.error('❌ No valid email addresses found in recipients array');
      return { success: false, error: 'No valid recipient email addresses' };
    }

    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: recipientEmails, // Edge function converts this to BCC field
          subject: subject,
          type: notificationType || 'notification',
          data: {
            title: subject,
            message: message,
            priority: priority || 'medium',
            timestamp: new Date()?.toISOString()
          }
        })
      }
    );

    const result = await response?.json();
    
    if (!response?.ok) {
      console.error('❌ Notification email sending failed:', result);
      return { success: false, error: result?.error };
    }

    console.log('✅ Notification emails sent successfully via BCC');
    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending notification emails:', error);
    return { success: false, error: error?.message };
  }
};

const notificationService = {
  sendNotificationEmails,
  sendFileUploadNotification,
  sendTaskCreationNotification,
  sendTaskStatusChangeNotification,
  sendTaskDueDateReminder,
  checkAndSendTaskDueReminders,
  sendMeetingReminder,
  checkAndSendMeetingReminders,
  sendTaskSMS,
  createNotification,
  getTeamNotifications,
  getUserNotifications,
  getTeamNotificationsLast30Days,
  markNotificationsAsSeen,
  getUnseenNotificationCount,
  deleteTaskNotifications,
  createFileUploadNotification,
  createStageCompletionNotification,
  createDeadlineWarningNotification,
  createMeetingNotificationForAdmin
};

export default notificationService;